import { createConsumer } from '@rails/actioncable';
import Service from '@ember/service';

// This service manages the single ActionCable WebSocket connection for the entire app.
// Apps (tio-admin, tio-employee) should configure this service once at startup.
// Other services should only call subscribe()/unsubscribe().

type SubscriptionType = ReturnType<ReturnType<typeof createConsumer>['subscriptions']['create']>;

type SubscriptionCallbacks = {
  received?: (data: unknown) => void;
  connected?: () => void;
  disconnected?: () => void;
};

type SubscriptionRecord = {
  channelParams: object;
  callbacks: SubscriptionCallbacks;
  subscription: SubscriptionType;
};

export default class CableService extends Service {
  consumer?: ReturnType<typeof createConsumer>;

  // Track subscriptions by channel identifier, storing full record for reconnection
  private subscriptions = new Map<string, SubscriptionRecord>();

  // Connection config - set once by app initializer
  private apiHost?: string;
  private wsHost?: string;
  private apiKey?: string;
  private token?: string;

  #disabled = false;

  /**
   * Hard off-switch: while true, configure()/connect() are no-ops, so NO code
   * path (route setup, the TioAppearance presence component, rebuild-index,
   * multi-document-appearance) can open a socket to the API. The app sets this
   * for sessions that must not produce live traffic — e.g. tio-employee demo
   * mode, where everything on screen is fixture data and a live presence
   * socket would both leak the session token's traffic and show real users.
   *
   * Flipping it on also tears down an already-open consumer, so arming demo
   * mid-SPA-session (no full reload) cannot leave a live socket behind.
   */
  get disabled(): boolean {
    return this.#disabled;
  }

  set disabled(value: boolean) {
    this.#disabled = value;
    if (value) {
      this.disconnect();
    }
  }

  /**
   * Configure the cable service with connection details.
   * Should be called once by the app (tio-admin/tio-employee) at startup.
   * @param wsHost - Optional dedicated WebSocket host (e.g. wss://tio-api-stg-ws.tio.works).
   *                 Falls back to apiHost if not provided.
   */
  configure(apiHost: string, apiKey: string, token: string, wsHost?: string) {
    if (this.disabled) {
      return;
    }
    this.apiHost = apiHost;
    this.wsHost = wsHost;
    this.apiKey = apiKey;
    this.token = token;
  }

  /**
   * Update the auth token. The next time ActionCable reconnects (including
   * its built-in auto-reconnect) it will use this token because the consumer
   * was created with a dynamic URL function.
   */
  updateToken(token: string) {
    this.token = token;
  }

  /**
   * Build the cable URL using the current token. Passed as a function to
   * createConsumer so ActionCable's built-in reconnect always uses a fresh token.
   * Uses wsHost (dedicated WebSocket host) if configured, otherwise falls back to apiHost.
   */
  private buildUrl = () => {
    const { wsHost, apiHost, apiKey, token } = this;
    const host = wsHost || apiHost;

    if (!host || !apiKey || !token) {
      throw new Error(
        'CableService: buildUrl called before configuration or with missing credentials.'
      );
    }

    const KEY = encodeURIComponent(apiKey);
    const TOKEN = encodeURIComponent(token);
    return `${host}/cable?apiKey=${KEY}&token=${TOKEN}`;
  };

  /**
   * Check if cable is configured
   */
  get isConfigured(): boolean {
    return !!(this.apiHost && this.apiKey && this.token);
  }

  /**
   * Ensure connection is established. Called automatically by subscribe().
   */
  private ensureConnected() {
    if (this.disabled || this.consumer) return;

    if (!this.isConfigured) {
      console.error('CableService: Not configured. Call configure() first.');
      return;
    }

    // Pass a function so ActionCable calls it on every reconnect attempt,
    // always picking up the latest token from updateToken().
    this.consumer = createConsumer(this.buildUrl);
  }

  /**
   * @deprecated Use configure() at app startup instead.
   * Kept for backwards compatibility during migration.
   */
  connect(apiHost: string, apiKey: string, token: string, wsHost?: string) {
    if (!this.isConfigured) {
      this.configure(apiHost, apiKey, token, wsHost);
    }
    this.ensureConnected();
  }

  /**
   * Create a subscription or return existing one if already subscribed.
   * Prevents duplicates based on channel identifier.
   * Note: If a subscription already exists, new callbacks are ignored.
   * Unsubscribe first if you need to change callbacks.
   * Automatically establishes connection if needed.
   *
   * WARNING: Do not call .unsubscribe() directly on the returned subscription
   * as it bypasses registry cleanup. Use cable.unsubscribe(channelParams) instead.
   */
  subscribe(channelParams: object, callbacks: SubscriptionCallbacks): SubscriptionType | undefined {
    this.ensureConnected();

    if (!this.consumer) {
      console.error('CableService: Cannot subscribe - not configured');
      return undefined;
    }

    const identifier = this.getIdentifier(channelParams);

    // Return existing subscription if already subscribed
    const existingRecord = this.subscriptions.get(identifier);
    if (existingRecord) {
      // Warn if new callbacks differ from existing ones (they will be ignored)
      if (
        (callbacks.received && existingRecord.callbacks.received !== callbacks.received) ||
        (callbacks.connected && existingRecord.callbacks.connected !== callbacks.connected) ||
        (callbacks.disconnected && existingRecord.callbacks.disconnected !== callbacks.disconnected)
      ) {
        console.warn(
          'CableService: subscribe() called with different callbacks for an existing subscription. ' +
            'The new callbacks will be ignored. Unsubscribe first if you want to change callbacks.'
        );
      }
      return existingRecord.subscription;
    }

    const subscription = this.consumer.subscriptions.create(channelParams, callbacks);
    this.subscriptions.set(identifier, {
      channelParams,
      callbacks,
      subscription,
    });

    return subscription;
  }

  /**
   * Generate a stable identifier for channel params.
   * Sorts object keys to ensure consistent ordering.
   */
  private getIdentifier(channelParams: object): string {
    return this.stableStringify(channelParams);
  }

  /**
   * Deterministically stringify an object by sorting its keys.
   * Handles nested objects and arrays.
   */
  private stableStringify(obj: unknown): string {
    if (obj === null || typeof obj !== 'object') {
      return JSON.stringify(obj);
    }
    if (Array.isArray(obj)) {
      return '[' + obj.map((item) => this.stableStringify(item)).join(',') + ']';
    }
    const keys = Object.keys(obj as Record<string, unknown>).sort();
    const keyValuePairs = keys.map((key) => {
      const value = (obj as Record<string, unknown>)[key];
      return JSON.stringify(key) + ':' + this.stableStringify(value);
    });
    return '{' + keyValuePairs.join(',') + '}';
  }

  /**
   * Unsubscribe from a channel
   */
  unsubscribe(channelParams: object) {
    const identifier = this.getIdentifier(channelParams);
    const record = this.subscriptions.get(identifier);

    if (record) {
      record.subscription.unsubscribe();
      this.subscriptions.delete(identifier);
    }
  }

  /**
   * Perform an action on a subscription
   * @param channelParams - The channel params used when subscribing
   * @param action - The action name to perform
   * @param data - The data to send with the action
   */
  perform(channelParams: object, action: string, data?: object): boolean {
    const identifier = this.getIdentifier(channelParams);
    const record = this.subscriptions.get(identifier);

    if (record) {
      record.subscription.perform(action, data || {});
      return true;
    }

    console.warn('CableService: No subscription found for perform()', channelParams);
    return false;
  }

  /**
   * Disconnect and clean up all subscriptions
   */
  disconnect() {
    if (this.consumer) {
      this.consumer.disconnect();
      this.consumer = undefined;
      this.subscriptions.clear();
    }
  }
}
